import * as React from 'react';
import { Check, LoaderCircle } from 'lucide-react';
import { Button, ButtonProps } from './button';
import './AnimatedButton.css';

export type AnimatedButtonState = 'idle' | 'loading' | 'success' | 'error';

export interface AnimatedButtonProps extends ButtonProps {
  state: AnimatedButtonState;
  loadingText?: string;
  successText?: string;
  successDuration?: number;
}

const AnimatedButton = React.forwardRef<HTMLButtonElement, AnimatedButtonProps>(
  (
    {
      state,
      loadingText,
      successText = 'Saved!',
      successDuration = 1800,
      children,
      className,
      disabled,
      ...buttonProps
    },
    ref
  ) => {
    const [internalState, setInternalState] = React.useState<AnimatedButtonState>(state);

    React.useEffect(() => {
      setInternalState(state);

      if (state === 'success' && successDuration > 0) {
        const timer = setTimeout(() => {
          setInternalState('idle');
        }, successDuration);

        return () => clearTimeout(timer);
      }
    }, [state, successDuration]);

    const isLoading = internalState === 'loading';
    const isSuccess = internalState === 'success';

    const confettiColors = [
      '#fbbf24',
      '#f59e0b',
      '#34d399',
      '#10b981',
      '#60a5fa',
      '#3b82f6'
    ];

    return (
      <Button
        ref={ref}
        className={`relative transition-transform duration-300 ease-out ${
          isSuccess ? 'scale-100' : isLoading ? 'scale-100' : ''
        } ${className || ''}`}
        disabled={disabled || isLoading || isSuccess}
        {...buttonProps}
      >
        {/* Success background animations */}
        {isSuccess && (
          <>
            <span
              className="absolute inset-0 bg-green-400 animate-ping opacity-75"
              style={{ animationDuration: '1s', animationIterationCount: '1' }}
            />
            <span className="absolute inset-0 bg-green-300 animate-pulse opacity-50" />
          </>
        )}

        {/* Idle state content */}
        <span
          className={`relative inline-flex items-center gap-2 transition-all duration-300 ${
            isSuccess || isLoading ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
          }`}
        >
          {children}
        </span>

        {isLoading && (
          <span className="absolute inset-0 flex items-center justify-center gap-2 animated-button-fade-in">
            <LoaderCircle className="animate-spin" />
            <span>{loadingText || children}</span>
          </span>
        )}

        {isSuccess && (
          <>
            <span className="absolute inset-0 flex items-center justify-center gap-2">
              <span className="relative inline-flex items-center gap-2">
                <span className="relative">
                  <Check className="w-5 h-5 text-current animated-button-check" />

                  {[...Array(6)].map((_, i) => (
                    <span
                      key={i}
                      className={`animated-button-confetti animated-button-confetti-${i}`}
                      style={{
                        backgroundColor: confettiColors[i % 6]
                      }}
                    />
                  ))}
                </span>
                <span className="animated-button-slide-in">{successText}</span>
              </span>
            </span>
          </>
        )}
      </Button>
    );
  }
);

AnimatedButton.displayName = 'AnimatedButton';

export { AnimatedButton };